import type { AutoResolvedSuggestion } from '@this+that/models';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';

import { AutoResolvedRowState } from '../../../../enums/autoResolvedRowState';
import {
  AUTO_RESOLVED_DAY_KEY_FORMAT,
  AUTO_RESOLVED_DAYS_AGO_KEY,
  AUTO_RESOLVED_SEEN_STORAGE_KEY_PREFIX,
  WEEKDAY_SHORT_FORMAT,
} from '../../../../utils/constants/autoResolvedSuggestions';

export interface AutoResolvedSuggestionGroup {
  key: string;
  label: string;
  items: AutoResolvedSuggestion[];
}

const resolvedAtOf = (item: AutoResolvedSuggestion): number =>
  item.entry.resolvedAt ?? 0;

const dayKeyOf = (item: AutoResolvedSuggestion): string =>
  dayjs(resolvedAtOf(item)).format(AUTO_RESOLVED_DAY_KEY_FORMAT);

function dayGroupLabel(dayKey: string, nowMs: number, t: TFunction): string {
  const day = dayjs(dayKey);
  if (day.isSame(nowMs, 'day')) return t('dateFormats.today');
  if (day.add(1, 'day').isSame(nowMs, 'day')) return t('dateFormats.yesterday');
  const count = dayjs(nowMs).startOf('day').diff(day.startOf('day'), 'day');
  return t(AUTO_RESOLVED_DAYS_AGO_KEY, { count });
}

export function groupAutoResolvedByDay(
  items: AutoResolvedSuggestion[],
  nowMs: number,
  t: TFunction,
): AutoResolvedSuggestionGroup[] {
  const byDay = new Map<string, AutoResolvedSuggestion[]>();
  [...items]
    .sort((a, b) => resolvedAtOf(b) - resolvedAtOf(a))
    .forEach((item) => {
      const key = dayKeyOf(item);
      byDay.set(key, [...(byDay.get(key) ?? []), item]);
    });
  return [...byDay.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, groupItems]) => ({
      key,
      label: dayGroupLabel(key, nowMs, t),
      items: groupItems,
    }));
}

export function selectReviewCount(
  items: AutoResolvedSuggestion[],
  rowStates: Record<string, AutoResolvedRowState>,
): number {
  return items.filter((item) => {
    const state = rowStates[item.entry.id];
    return (
      state !== AutoResolvedRowState.UNDONE &&
      state !== AutoResolvedRowState.CONFIRMED
    );
  }).length;
}

export function hasAutoResolvedTabs(items: AutoResolvedSuggestion[]): boolean {
  return items.length > 0;
}

export function formatBoardShortTime(
  ts: number,
  nowMs: number,
  t: TFunction,
): string {
  const date = dayjs(ts);
  if (date.isSame(nowMs, 'day')) {
    return date.format(t('dateFormats.hoursMinutes'));
  }
  if (date.add(1, 'day').isSame(nowMs, 'day'))
    return t('dateFormats.yesterday');
  if (date.isSame(nowMs, 'week')) return date.format(WEEKDAY_SHORT_FORMAT);
  return date.format(t('dateFormats.shortDateWithMonth'));
}

export function newestResolvedAt(
  items: AutoResolvedSuggestion[],
): number | null {
  const stamps = items
    .map((item) => item.entry.resolvedAt)
    .filter((value): value is number => typeof value === 'number');
  return stamps.length ? Math.max(...stamps) : null;
}

export function buildSeenStorageKey(
  userId: string,
  taskGroupId: string,
): string {
  return `${AUTO_RESOLVED_SEEN_STORAGE_KEY_PREFIX}:${userId}:${taskGroupId}`;
}
